import {
  EnvironmentProviders,
  Injectable,
  inject,
  provideEnvironmentInitializer,
} from '@angular/core';
import {
  SignalVaultAngularConfig,
  SignalVaultLogProperties,
  SignalVaultLogger,
} from '@mackao/signalvault-angular';
import { environment } from '../../environments/environment';

const MAX_SNAPSHOT_DEPTH = 3;
const MAX_SNAPSHOT_KEYS = 30;
const MAX_PREVIEW_LENGTH = 1000;
const REDACTED_KEY_PATTERN =
  /authorization|cookie|password|token|apikey|api-key|secret|set-cookie/i;

export const signalVaultConfig: SignalVaultAngularConfig = {
  endpoint: `${environment.apiDomain.replace(/\/+$/, '')}/signalvault`,
  captureDurations: true,
  captureRouteDurations: true,
  maxPropertyDepth: 6,
  maxStringLength: 4000,
};

@Injectable({
  providedIn: 'root',
})
export class SignalVaultLogService {
  private signalVaultLogger = inject(SignalVaultLogger);

  error(
    message: string,
    error: unknown,
    properties: SignalVaultLogProperties = {},
  ) {
    this.signalVaultLogger.error(error, {
      ...properties,
      messageTemplate: message,
    });
  }
}

export function logBootstrapError(error: unknown) {
  const logger = new SignalVaultLogger(signalVaultConfig);

  logger.error(error, {
    messageTemplate: 'Error bootstrapping Angular application',
    sourceContext: 'Angular.Bootstrap',
  });

  void logger.flush();
}

export function provideDetailedGlobalErrorLogging(): EnvironmentProviders {
  return provideEnvironmentInitializer(() => {
    const logger = inject(SignalVaultLogger);

    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener('unhandledrejection', (event) => {
      const rejection = describeRejectedValue(event.reason);

      logger.error(createUnhandledRejectionError(event.reason, rejection), {
        eventType: 'UnhandledPromiseRejectionDiagnostic',
        sourceContext: 'Angular.Window',
        messageTemplate: 'Unhandled Angular promise rejection with diagnostics',
        rejection,
        browserEvent: {
          cancelable: event.cancelable,
          defaultPrevented: event.defaultPrevented,
          isTrusted: event.isTrusted,
          type: event.type,
        },
        userAgent: navigator.userAgent,
      });
    });
  });
}

export function describeRejectedValue(value: unknown): Record<string, unknown> {
  return describeRejectedValueInternal(value, new WeakSet());
}

function describeRejectedValueInternal(
  value: unknown,
  seen: WeakSet<object>,
): Record<string, unknown> {
  const details: Record<string, unknown> = {
    valueType: value === null ? 'null' : typeof value,
    preview: previewValue(value),
  };

  if (value === null || value === undefined) {
    return details;
  }

  if (value instanceof Error) {
    if (seen.has(value)) {
      return {
        ...details,
        circular: true,
      };
    }

    seen.add(value);

    details['name'] = value.name;
    details['message'] = value.message;
    details['stack'] = value.stack;

    if ('cause' in value) {
      details['cause'] = describeRejectedValueInternal(value.cause, seen);
    }
  }

  if (typeof value !== 'object' && typeof value !== 'function') {
    return details;
  }

  const objectValue = value as object;

  details['objectTag'] = Object.prototype.toString.call(objectValue);
  details['constructorName'] = safeConstructorName(objectValue);
  details['ownPropertyNames'] = safeOwnPropertyNames(objectValue);
  details['ownEnumerableKeys'] = safeOwnEnumerableKeys(objectValue);
  details['ownSymbolKeys'] = safeOwnSymbolKeys(objectValue);
  details['snapshot'] = snapshotObject(objectValue, new WeakSet(), 0);

  return details;
}

function createUnhandledRejectionError(
  reason: unknown,
  rejection: Record<string, unknown>,
): Error {
  if (reason instanceof Error) {
    return reason;
  }

  const valueType = String(rejection['valueType'] ?? typeof reason);
  const constructorName = String(rejection['constructorName'] ?? '');
  const preview = String(rejection['preview'] ?? '');
  const suffix = constructorName ? ` (${constructorName})` : '';
  const error = new Error(
    `Unhandled promise rejection: ${valueType}${suffix}: ${preview}`,
  );

  error.name = 'UnhandledPromiseRejection';

  return error;
}

function snapshotObject(
  value: object,
  seen: WeakSet<object>,
  depth: number,
): unknown {
  if (seen.has(value)) {
    return '[Circular]';
  }

  if (depth >= MAX_SNAPSHOT_DEPTH) {
    return `[${Object.prototype.toString.call(value)}]`;
  }

  seen.add(value);

  const snapshot: Record<string, unknown> = {};

  for (const key of safeOwnPropertyNames(value).slice(0, MAX_SNAPSHOT_KEYS)) {
    if (REDACTED_KEY_PATTERN.test(key)) {
      snapshot[key] = '[Redacted]';
      continue;
    }

    try {
      const descriptor = Object.getOwnPropertyDescriptor(value, key);

      if (descriptor && !('value' in descriptor)) {
        snapshot[key] = '[Accessor]';
        continue;
      }

      snapshot[key] = snapshotValue(
        (value as Record<string, unknown>)[key],
        seen,
        depth + 1,
      );
    } catch (error: unknown) {
      snapshot[key] = `[Unreadable: ${previewValue(error)}]`;
    }
  }

  seen.delete(value);

  return snapshot;
}

function snapshotValue(
  value: unknown,
  seen: WeakSet<object>,
  depth: number,
): unknown {
  if (value === null || value === undefined) {
    return value;
  }

  if (typeof value === 'string') {
    return truncate(value, MAX_PREVIEW_LENGTH);
  }

  if (typeof value !== 'object' && typeof value !== 'function') {
    return value;
  }

  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
      cause: 'cause' in value ? snapshotValue(value.cause, seen, depth) : null,
    };
  }

  return snapshotObject(value as object, seen, depth);
}

function safeOwnPropertyNames(value: object): string[] {
  try {
    return Object.getOwnPropertyNames(value);
  } catch {
    return [];
  }
}

function safeOwnEnumerableKeys(value: object): string[] {
  try {
    return Object.keys(value);
  } catch {
    return [];
  }
}

function safeOwnSymbolKeys(value: object): string[] {
  try {
    return Object.getOwnPropertySymbols(value).map((symbol) =>
      symbol.toString(),
    );
  } catch {
    return [];
  }
}

function safeConstructorName(value: object): string | undefined {
  try {
    return value.constructor?.name;
  } catch {
    return undefined;
  }
}

function previewValue(value: unknown): string {
  if (typeof value === 'string') {
    return truncate(value, MAX_PREVIEW_LENGTH);
  }

  if (value instanceof Error) {
    return truncate(`${value.name}: ${value.message}`, MAX_PREVIEW_LENGTH);
  }

  try {
    return truncate(JSON.stringify(value), MAX_PREVIEW_LENGTH);
  } catch {
    return truncate(String(value), MAX_PREVIEW_LENGTH);
  }
}

function truncate(value: string | undefined, maxLength: number): string {
  if (!value) {
    return '';
  }

  return value.length <= maxLength
    ? value
    : `${value.slice(0, maxLength)}...`;
}
